import React, { useState, useCallback, useRef } from "react";
import { useRecoilValue, useRecoilState } from "recoil";
import { userState } from "../../../../../recoil/auth";
import {
  dashboardDataState,
  dashboardLoadingState,
  selectedAdState,
} from "../../../../../recoil/home/tabs/dashboard/dashboardState";
import { getMemberAdAPI, getMissionActionAPI } from "../../../../../api";

// 목데이터
const mockAds = [
  {
    ad_id: 1234567891, // ✅ ad_id로 변경
    company: "올려줌 공식 스토어",
    title: "올려줌 S22 울트라 폰케이스",
    mainKeyword: "휴대폰 케이스", // ✅ mainKeyword 추가
    initialRank: 58, // ✅ initialRank 추가
    currentRank: 2, // ✅ currentRank 추가
    details: "3,000회 x 11일",
    period: "8.10 ~ 8.19",
    status: "진행중",
    statusType: "active",
    imageUrl: "/header_icon.ico",
  },
  {
    ad_id: 1234567892, // ✅ ad_id로 변경
    company: "올려줌 공식 스토어",
    title: "올려줌 홍삼정 10ml x 30포",
    mainKeyword: "홍삼", // ✅ mainKeyword 추가
    initialRank: 45, // ✅ initialRank 추가
    currentRank: 12, // ✅ currentRank 추가
    details: "2,500회 x 11일",
    period: "8.12 ~ 8.22",
    status: "진행중",
    statusType: "active",
    imageUrl: "/header_icon.ico",
  },
  {
    ad_id: 1234567893, // ✅ ad_id로 변경
    title: "올려줌 헤어 신촌점",
    mainKeyword: "신촌 미용실", // ✅ mainKeyword 추가
    initialRank: 58, // ✅ initialRank 추가
    currentRank: 2, // ✅ currentRank 추가
    details: "3,000회 x 10일",
    period: "8.28 ~ 9.6",
    status: "진행중",
    statusType: "active",
    imageUrl: "/header_icon.ico",
  },
  {
    ad_id: 1234567894, // ✅ ad_id로 변경
    title: "올려줌 카페 서울시청점",
    mainKeyword: "서울 카페", // ✅ mainKeyword 추가
    initialRank: 45, // ✅ initialRank 추가
    currentRank: 12, // ✅ currentRank 추가
    details: "3,000회 x 10일",
    period: "8.28 ~ 9.6",
    status: "진행중",
    statusType: "active",
    imageUrl: "/header_icon.ico",
  },
];

const MyAds = () => {
  const user = useRecoilValue(userState);
  const [dashboardData, setDashboardData] = useRecoilState(dashboardDataState);
  const loading = useRecoilValue(dashboardLoadingState);
  const [selectedAd, setSelectedAd] = useRecoilState(selectedAdState);
  const { ads, pagination, error } = dashboardData;

  // 무한 스크롤을 위한 상태 (allAds 제거, ads 직접 사용)
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observer = useRef();

  // 드롭다운을 위한 상태
  const [selectedAction, setSelectedAction] = useState("전체");
  const [actionOptions, setActionOptions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);

  // 로그인 상태에 따라 데이터 결정
  const isLoggedIn = !!user;
  const displayAds = isLoggedIn ? ads : mockAds;
  const displayLoading = isLoggedIn ? loading : false;
  const displayError = isLoggedIn ? error : null;

  // ✅ 첫 번째 광고를 기본 선택 (로그인/로그아웃 모두)
  React.useEffect(() => {
    if (Array.isArray(displayAds) && displayAds.length > 0 && !selectedAd) {
      // 로그인 상태에 따라 적절한 광고 객체 선택
      const firstAd = isLoggedIn
        ? ads.find((apiAd) => apiAd._id === displayAds[0]?.id) || ads[0]
        : displayAds[0];

      if (firstAd) {
        setSelectedAd(firstAd);
      }
    }
  }, [displayAds, selectedAd, setSelectedAd, isLoggedIn, ads]);

  // 광고 선택 핸들러
  const handleAdSelect = (ad) => {
    setSelectedAd(ad);
  };

  // ✅ 광고 ID 비교 함수 (안전한 비교)
  const isAdSelected = (ad) => {
    if (!selectedAd) return false;

    // 로그인 상태에 따른 비교
    if (isLoggedIn) {
      // API 데이터의 경우 _id로 비교
      if (selectedAd._id && ad.id) {
        return selectedAd._id === ad.id;
      }
      if (selectedAd._id && ad._id) {
        return selectedAd._id === ad._id;
      }
    } else {
      // 목데이터의 경우 ad_id로 비교
      if (selectedAd.ad_id && ad.ad_id) {
        return selectedAd.ad_id === ad.ad_id;
      }
    }

    return false;
  };

  // 액션 옵션들 로딩
  const loadActionOptions = useCallback(async () => {
    if (!isLoggedIn) return;

    setLoadingActions(true);
    try {
      const response = await getMissionActionAPI();
      if (response.data?.data) {
        setActionOptions(response.data.data);
      }
    } catch (error) {
      console.error("액션 옵션 로딩 실패:", error);
    } finally {
      setLoadingActions(false);
    }
  }, [isLoggedIn]);

  // 드롭다운 변경 핸들러
  const handleActionChange = async (actionName) => {
    setSelectedAction(actionName);

    // 선택된 액션에 따라 광고 필터링
    if (!user?.id) return;

    // 상태 초기화
    setCurrentPage(1);
    setHasMore(true);
    setLoadingMore(true);

    try {
      let missionTemplate = null;

      if (actionName !== "전체") {
        // 선택된 액션의 ma_id 찾기
        const selectedActionData = actionOptions.find(
          (action) => action.name === actionName
        );
        if (selectedActionData) {
          missionTemplate = selectedActionData.ma_id;
        }
      }

      // API 호출 파라미터 설정
      const apiParams = { status: "APPROVED" };
      if (missionTemplate) {
        apiParams.missionTemplate = missionTemplate;
      }

      const response = await getMemberAdAPI(user.id, 1, 10, apiParams);

      if (response.data?.data) {
        // dashboardData.ads를 새로운 데이터로 교체
        setDashboardData((prev) => ({
          ...prev,
          ads: response.data.data,
        }));
        setHasMore(
          response.data.pagination
            ? 1 < response.data.pagination.totalPages
            : false
        );
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("액션별 광고 로딩 실패:", error);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  // 다음 페이지 로딩
  const loadMoreAds = useCallback(async () => {
    if (!user?.id || loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;

      // 선택된 액션에 따른 파라미터 설정
      let missionTemplate = null;
      if (selectedAction !== "전체") {
        const selectedActionData = actionOptions.find(
          (action) => action.name === selectedAction
        );
        if (selectedActionData) {
          missionTemplate = selectedActionData.ma_id;
        }
      }

      const apiParams = { status: "APPROVED" };
      if (missionTemplate) {
        apiParams.missionTemplate = missionTemplate;
      }

      const response = await getMemberAdAPI(user.id, nextPage, 10, apiParams);

      if (response.data?.data && response.data.data.length > 0) {
        // 중복 광고 방지: 기존에 없는 광고만 추가
        setDashboardData((prev) => {
          const existingAdIds = new Set(prev.ads.map((ad) => ad._id));
          const newAds = response.data.data.filter(
            (ad) => !existingAdIds.has(ad._id)
          );

          if (newAds.length === 0) {
            // 새로운 광고가 없으면 더 이상 로드할 필요 없음
            setHasMore(false);
            return prev;
          }

          return {
            ...prev,
            ads: [...prev.ads, ...newAds],
          };
        });

        setCurrentPage(nextPage);
        setHasMore(nextPage < (response.data.pagination?.totalPages || 0));
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("추가 광고 로딩 실패:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [
    user?.id,
    currentPage,
    loadingMore,
    hasMore,
    selectedAction,
    actionOptions,
  ]);

  // 무한 스크롤 옵저버 설정
  const lastAdElementRef = useCallback(
    (node) => {
      if (loadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreAds();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loadingMore, hasMore, loadMoreAds]
  );

  // 초기 데이터 설정
  React.useEffect(() => {
    if (isLoggedIn && Array.isArray(ads)) {
      setCurrentPage(1);
      setHasMore(pagination ? currentPage < pagination.totalPages : false);
    }
  }, [isLoggedIn, ads, pagination]);

  // 액션 옵션들 로딩
  React.useEffect(() => {
    loadActionOptions();
  }, [loadActionOptions]);

  // API 응답 데이터를 UI에 맞게 변환하는 함수
  const transformAdData = (apiAd) => {
    const startDate = new Date(apiAd.startDate);
    const endDate = new Date(apiAd.endDate);

    return {
      id: apiAd._id,
      company: apiAd.storeName || "",
      title: apiAd.missionName || "광고명 없음",
      details: `${apiAd.dailyQuantity}회 x ${Math.ceil(
        (endDate - startDate) / (1000 * 60 * 60 * 24)
      )}일`,
      period: `${startDate.getMonth() + 1}.${startDate.getDate()} ~ ${
        endDate.getMonth() + 1
      }.${endDate.getDate()}`,
      status: getStatusText(apiAd.status),
      statusType: getStatusType(apiAd.status),
      imageUrl: apiAd.imageUrl,
    };
  };

  // 상태 텍스트 변환
  const getStatusText = (status) => {
    const statusMap = {
      PENDING: "승인대기",
      APPROVED: "진행중",
      PAUSED: "일시정지",
      REJECTED: "거절",
      REFUND: "환불",
      DONE: "종료",
    };
    return statusMap[status] || status;
  };

  // 상태 타입 변환
  const getStatusType = (status) => {
    const typeMap = {
      PENDING: "pending",
      APPROVED: "active",
      PAUSED: "paused",
      REJECTED: "rejected",
      REFUND: "refund",
      DONE: "done",
    };
    return typeMap[status] || "unknown";
  };

  // ads가 배열인지 확인하고 변환 (allAds 대신 ads 직접 사용)
  const transformedAds = isLoggedIn
    ? Array.isArray(ads)
      ? ads.map(transformAdData)
      : []
    : displayAds;

  if (displayLoading) {
    return (
      <div className="dashboard-widget-card">
        <div className="dashboard-card-header">
          <h3>진행중인 광고</h3>
        </div>
        <div style={{ padding: "20px", textAlign: "center" }}>로딩 중...</div>
      </div>
    );
  }

  if (displayError) {
    return (
      <div className="dashboard-widget-card">
        <div className="dashboard-card-header">
          <h3>진행중인 광고</h3>
        </div>
        <div style={{ padding: "20px", textAlign: "center", color: "red" }}>
          오류: {displayError}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-widget-card">
      <div className="dashboard-card-header">
        <h3>진행중인 광고</h3>
        <select
          value={selectedAction}
          onChange={(e) => handleActionChange(e.target.value)}
          className="dashboard-ads-filter"
          disabled={loadingActions}
        >
          <option value="전체">전체</option>
          {actionOptions.map((action) => (
            <option key={action._id} value={action.name}>
              {action.name}
            </option>
          ))}
        </select>
      </div>
      <ul className="dashboard-ads-list">
        {transformedAds.length > 0 ? (
          transformedAds.map((ad, index) => {
            const isSelected = selectedAd && isAdSelected(ad);
            const isLastElement = index === transformedAds.length - 1;
            return (
              <li
                key={`ad-${ad.id || ad.ad_id || index}-${index}`}
                ref={isLastElement ? lastAdElementRef : null}
                className={`dashboard-ad-item ${isSelected ? "selected" : ""}`}
                onClick={() =>
                  handleAdSelect(
                    isLoggedIn ? ads.find((apiAd) => apiAd._id === ad.id) : ad
                  )
                }
                style={{
                  cursor: "pointer",
                  border: isSelected
                    ? "2px solid #007bff"
                    : "1px solid #e0e0e0",
                  borderRadius: "16px",
                  backgroundColor: isSelected ? "#f8f9ff" : "transparent",
                  padding: isSelected ? "16px" : "12px",
                }}
              >
                {ad.imageUrl ? (
                  <img src={ad.imageUrl} alt={ad.title} className="ad-image" />
                ) : (
                  <div className="dashboard-ad-image-placeholder"></div>
                )}
                <div className="dashboard-ad-info">
                  <span className="dashboard-ad-company">{ad.company}</span>
                  <span className="dashboard-ad-title">{ad.title}</span>
                  <span className="dashboard-ad-details">{ad.details}</span>
                </div>
                <div className="dashboard-ad-status-container">
                  <span className="dashboard-ad-period">{ad.period}</span>
                  <span className={`dashboard-status-badge ${ad.statusType}`}>
                    {ad.status}
                  </span>
                </div>
              </li>
            );
          })
        ) : (
          <li
            key="no-ads"
            style={{ padding: "20px", textAlign: "center", color: "#666" }}
          >
            광고가 없습니다.
          </li>
        )}
        {loadingMore && (
          <li
            key="loading-more"
            style={{ padding: "20px", textAlign: "center", color: "#666" }}
          >
            추가 광고를 불러오는 중...
          </li>
        )}
      </ul>
    </div>
  );
};

export default MyAds;
